import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useIsFetching } from 'react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@parrot-software/design-system';

import { useReportChartBlock } from '@reports/presentation/hooks';
import { useReportsState } from '@reports/presentation/contexts';
import { ReportBlockContainer } from '@reports/presentation/components/base/ReportBlockContainer';
import { ReportEmptyState } from '@reports/presentation/components/base/ReportEmptyState';
import { ReportChartStates } from '@reports/presentation/components/base/ReportChartStates';
import { ReportBarChart } from '@reports/presentation/components/base/ReportBarChart';
import { ReportPieChart } from '@reports/presentation/components/base/ReportPieChart';
import { ReportHorizontalBarChart } from '@reports/presentation/components/base/ReportHorizonatalBarChart';
import { ReportLineChart } from '@reports/presentation/components/base/ReportLineChart';
import { transformChartData } from '@reports/presentation/utils/chartDataTransformers';
import {
  createBarTooltipFormatter,
  createPieTooltipFormatter,
} from '@reports/presentation/utils/chartTooltipFormatters';
import {
  createTopLabelFormatter,
  createPieLabelRenderer,
  createBarLegendLabel,
  createBarChartConfig,
  createPieChartConfig,
} from '@reports/presentation/utils/chartLabelRenderers';
import { generateColorVariations } from '@reports/presentation/utils/colorVariations';
import { ReportChartTypes } from '@reports/domain/types';
import type { ChartBlockConfig } from '@reports/domain/value-objects/blocks';
import type { BlockDataRequestDto } from '@reports/application/dtos';

interface ReportChartBlockProps {
  blockConfig: ChartBlockConfig;
  request: BlockDataRequestDto;
}

/**
 * ReportChartBlock - Chart block component for reports
 *
 * Renders bar or pie charts using Recharts with proper formatting,
 * tooltips, and responsive layout. Handles loading, error, and empty states.
 * Gets data fetching capabilities from composition root.
 *
 * @param {ReportChartBlockProps} props - Component properties
 * @param {ChartBlockConfig} props.blockConfig - Chart configuration
 * @param {BlockDataRequestDto} props.request - Request parameters
 * @returns {React.ReactElement} Chart block component
 *
 * @example
 * <ReportChartBlock
 *   blockConfig={chartBlockConfig}
 *   request={{ startDate: new Date(), endDate: new Date(), filters: {} }}
 * />
 */
export const ReportChartBlock = ({ blockConfig, request }: ReportChartBlockProps) => {
  const { t } = useTranslation();
  const { state } = useReportsState();
  const isFiltersInitialized = state.isInitialized;
  const isFiltersLoading = useIsFetching(['reports', 'filter-catalog']) > 0;

  const { chartBlockData, chartBlockState, refetchChartBlock } = useReportChartBlock({
    request,
    blockConfig,
    enabled: isFiltersInitialized,
  });

  const showLoadingState = !isFiltersInitialized || isFiltersLoading || chartBlockState.isFetching;

  // Transform data for Recharts - only recalculate when data changes
  const chartData = useMemo(
    () => transformChartData(chartBlockData, t, { groupTopValues: blockConfig.options?.groupTopValues }),
    [chartBlockData, t, blockConfig.options?.groupTopValues],
  );

  // Check if all chart values are zero
  const areAllValuesZero = useMemo(() => {
    if (!chartData || chartData.length === 0) {
      return true;
    }
    return chartData.every((item) => item.value === 0);
  }, [chartData]);

  // Generate color variations for pie charts - only when needed
  const colorVariations = useMemo(() => {
    if (!chartBlockData || blockConfig.chartType !== ReportChartTypes.PIE) {
      return [];
    }
    const baseColor = blockConfig.options?.colors?.[0] || '#EB6668';
    return generateColorVariations(baseColor, chartBlockData.values.length);
  }, [chartBlockData, blockConfig.chartType, blockConfig.options?.colors]);

  // Create formatters - stable references, no need for useCallback
  const seriesConfig = blockConfig.seriesConfig[0];
  const seriesTooltipFormatter = createBarTooltipFormatter(seriesConfig, t);
  const pieTooltipFormatter = createPieTooltipFormatter(seriesConfig);
  const topLabelFormatter = createTopLabelFormatter(seriesConfig);
  const pieLabelRenderer = createPieLabelRenderer();
  const barLegendLabel = createBarLegendLabel(seriesConfig, t);
  const seriesColor = blockConfig.options?.colors?.[0] || '#EB6668';
  const seriesChartConfig = createBarChartConfig(barLegendLabel, seriesColor);
  const pieChartConfig = createPieChartConfig(chartData, colorVariations);

  if (showLoadingState) {
    return (
      <ReportChartStates
        description={blockConfig.description}
        state="loading"
        title={blockConfig.title}
        width={blockConfig.width}
      />
    );
  }

  if (chartBlockState.isError) {
    return (
      <ReportChartStates
        description={blockConfig.description}
        onRetry={refetchChartBlock}
        state="error"
        title={blockConfig.title}
        width={blockConfig.width}
      />
    );
  }

  if (areAllValuesZero) {
    return (
      <ReportChartStates
        description={blockConfig.description}
        state="empty"
        title={blockConfig.title}
        width={blockConfig.width}
      />
    );
  }

  // Render chart based on type
  const renderChart = () => {
    const showTooltip = blockConfig.options?.showTooltip ?? false;
    const showLegend = blockConfig.options?.showLegend ?? false;

    switch (blockConfig.chartType) {
      case ReportChartTypes.BAR:
        return (
          <ReportBarChart
            barColor={seriesColor}
            chartConfig={seriesChartConfig}
            chartData={chartData}
            format={seriesConfig.format}
            showLegend={showLegend}
            showTooltip={showTooltip}
            showTopBarLabels={blockConfig.options?.showTopBarLabels ?? false}
            tooltipFormatter={seriesTooltipFormatter}
            topLabelFormatter={topLabelFormatter}
          />
        );

      case ReportChartTypes.BAR_HORIZONTAL:
        return (
          <ReportHorizontalBarChart
            barColor={seriesColor}
            chartConfig={seriesChartConfig}
            chartData={chartData}
            format={seriesConfig.format}
            showLabels={blockConfig.options?.showLabels ?? false}
            showLegend={showLegend}
            showTooltip={showTooltip}
            showTopBarLabels={blockConfig.options?.showTopBarLabels ?? false}
            tooltipFormatter={seriesTooltipFormatter}
            topLabelFormatter={topLabelFormatter}
          />
        );

      case ReportChartTypes.PIE:
        return (
          <ReportPieChart
            chartConfig={pieChartConfig}
            chartData={chartData}
            colorVariations={colorVariations}
            labelRenderer={pieLabelRenderer}
            showLabels={blockConfig.options?.showLabels ?? false}
            showLegend={showLegend}
            showTooltip={showTooltip}
            tooltipFormatter={pieTooltipFormatter}
          />
        );

      case ReportChartTypes.LINE:
        return (
          <ReportLineChart
            chartConfig={seriesChartConfig}
            chartData={chartData}
            lineColor={seriesColor}
            showLegend={showLegend}
            showTooltip={showTooltip}
            tooltipFormatter={seriesTooltipFormatter}
          />
        );

      default:
        return <ReportEmptyState />;
    }
  };

  return (
    <ReportBlockContainer width={blockConfig.width}>
      <Card className="h-[364px]">
        {blockConfig.title && (
          <CardHeader>
            <CardTitle className="text-base font-medium">{t(blockConfig.title)}</CardTitle>
            {blockConfig.description && (
              <CardDescription className="mt-0!">{t(blockConfig.description)}</CardDescription>
            )}
          </CardHeader>
        )}
        <CardContent className="flex-1 py-0">{renderChart()}</CardContent>
      </Card>
    </ReportBlockContainer>
  );
};
